/**
 * Choosing which organization to work in.
 *
 * Custom claims hold ONE orgId, so belonging to two hospitals means switching
 * rather than seeing both at once. `selectOrganization` rewrites the claim,
 * which is why the session has to be refreshed afterwards — without that the
 * app keeps the old token until it expires, and every query silently reads
 * the previous organization's data.
 *
 * Reached automatically when somebody belongs to more than one, and from the
 * profile page when they want to move.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/useAuth';
import { call, isCallFailure } from '../../lib/callable';
import { ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './SelectOrg.module.css';

interface Organization {
  orgId?: string;
  id?: string;
  name?: string;
  orgName?: string;
  roles?: unknown;
  status?: string;
}

function orgId(org: Organization): string {
  return org.orgId ?? org.id ?? '';
}

function orgName(org: Organization): string {
  return org.orgName ?? org.name ?? orgId(org) ?? 'Unnamed organization';
}

function roleLabels(org: Organization): string {
  const roles = Array.isArray(org.roles) ? org.roles.filter(isRole) : [];
  return roles.length ? roles.map((r) => ROLE_DISPLAY_NAMES[r]).join(', ') : 'No role';
}

export function SelectOrgPage() {
  const { session, refresh } = useAuth();
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState<string | null>(null);

  const orgs = useQuery({
    queryKey: ['my-organizations'],
    retry: false,
    queryFn: () =>
      call<Record<string, never>, { organizations: Organization[] }>(
        'getUserOrganizations',
        {},
      ),
  });

  const select = useMutation({
    mutationFn: (id: string) => call<{ orgId: string }, unknown>('selectOrganization', { orgId: id }),
    onSuccess: async () => {
      // The claim has changed server-side; the cached token has not. Without
      // this the app reads the previous organization until the token expires.
      await refresh();
      navigate('/', { replace: true });
    },
  });

  if (orgs.isLoading) return <Spinner label="Loading your organizations" />;

  if (orgs.error) {
    return (
      <PageState title="Could not load your organizations" tone="alert">
        {isCallFailure(orgs.error) ? orgs.error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  const list = orgs.data?.organizations ?? [];

  if (list.length === 0) {
    return (
      <PageState title="You are not in an organization yet" tone="alert">
        Your account is not a member of any hospital or clinic on StandingDocs. An
        administrator there needs to invite you.
      </PageState>
    );
  }

  return (
    <div className={own.wrap}>
      <h1 className={styles.title}>Choose an organization</h1>
      <p className={styles.subtitle}>
        You belong to more than one. Everything you see — policies, tasks,
        approvals — comes from the one you pick, and you can switch again from
        your account page.
      </p>

      {select.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(select.error) ? select.error.message : 'Could not switch organization.'}
        </div>
      )}

      <ul className={own.list}>
        {list.map((org) => {
          const id = orgId(org);
          const current = session?.orgId === id;
          const busy = select.isPending && choosing === id;

          return (
            <li key={id}>
              <button
                type="button"
                className={`${own.card} ${current ? own.cardCurrent : ''}`}
                disabled={select.isPending}
                onClick={() => {
                  setChoosing(id);
                  select.mutate(id);
                }}
              >
                <span className={own.cardBody}>
                  <span className={own.cardName}>{orgName(org)}</span>
                  <span className={own.cardMeta}>{roleLabels(org)}</span>
                </span>
                <span className={own.cardAction}>
                  {busy ? 'Switching…' : current ? 'Current' : 'Switch'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
